import React from 'react';

import { getFloatField } from '@lender/utils';
import { DateIcon, DollarIcon, NoteIcon } from '@sharedCR/coreDesignSystem/icons';
import { compose } from 'redux';

import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { MIN_NUMBER_VALUE } from 'constants/number';
import { modifiedPendingChanges, PendingChangesActionType } from 'constants/pendingChanges';
import { StatusColor, StatusEnum } from 'constants/status';
import { COLOR_PALETTE } from 'constants/ui';
import { getConditionalArray } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';
import { getTextColorPair } from 'utils/milestoneStatus';

import DeleteActionButton from './DeleteActionButton';
import { CustomAdjustmentType } from './LockInfo.constants';
import { getTotalAmount } from './LockInfo.utils';

export const TotalAdjustmentsField = {
  NAME: 'Total Adjustments',
  KEY: 'totalAdjustmentAfterCap',
  PATH: 'adjustments.totalAdjustmentAfterCap',
};

const totalAdjustmentsStyles = {
  backgroundColor: COLOR_PALETTE.BLUE[2],
  color: getTextColorPair(COLOR_PALETTE.BLUE[2]),

  '& .MuiTypography-root': {
    fontWeight: 500,
  },
};

export const dataDisplayConfig = [
  {
    title: 'Lock Date',
    key: 'lockDate',
    icon: <DateIcon />,
    iconColor: StatusColor[StatusEnum.INFO],
  },
  {
    title: 'Lock Expiration',
    key: 'lockExpirationDate',
    icon: <DateIcon />,
    iconColor: StatusColor[StatusEnum.WARNING],
  },
  {
    title: 'Lock Term (Days)',
    key: 'lockTerm',
    icon: <NoteIcon />,
    iconColor: StatusColor[StatusEnum.INFO],
  },
  {
    title: 'Rate Sheet',
    key: 'rateSheetTitle',
    icon: <DateIcon />,
    iconColor: StatusColor[StatusEnum.WARNING],
    noTruncate: true,
  },
  {
    title: 'Buy Price',
    key: 'buyPrice',
    icon: <DollarIcon />,
    iconColor: StatusColor[StatusEnum.WARNING],
  },
  {
    title: 'Final Price',
    key: 'finalPrice',
    icon: <DollarIcon />,
    iconColor: StatusColor[StatusEnum.SUCCESS],
  },
];

export const lockInfoSectionFieldsKeys = [
  ...dataDisplayConfig.map(({ key }) => key),
  'adjustments',
];

const checkIsTotalAdjustmentsField = key => key === TotalAdjustmentsField.KEY;

export const getAdjustmentsTableConfig = ({
  sessionInfo,
  onDelete,
  isActionActive,
  isOBExternalLoanLock,
}) => ({
  name: {
    label: 'Adjustments Name',
    width: '53%',
    customStyles: ({ key }) => checkIsTotalAdjustmentsField(key) && totalAdjustmentsStyles,
  },
  amount: {
    label: 'Amount',
    width: '31%',
    customStyles: ({ key }) => checkIsTotalAdjustmentsField(key) && totalAdjustmentsStyles,
  },
  action: {
    label: 'Action',
    width: '16%',
    content: ({ key, type, id, pendingChangesActionType, isAdded }) => {
      const isPendingChanges = modifiedPendingChanges.includes(pendingChangesActionType) || isAdded;

      const isDisabled =
        isActionActive ||
        (!!sessionInfo?.type && sessionInfo.type !== type) ||
        (!!sessionInfo?.type &&
          sessionInfo.type === CustomAdjustmentType.FLOAT_DOWN &&
          type === CustomAdjustmentType.FLOAT_DOWN &&
          sessionInfo.deletedItems.length > 0) ||
        type === CustomAdjustmentType.AUTO_ADJUSTMENTS ||
        (isOBExternalLoanLock && !isPendingChanges);

      return (
        !checkIsTotalAdjustmentsField(key) && (
          <DeleteActionButton id={id} isDisabled={isDisabled} type={type} onDelete={onDelete} />
        )
      );
    },
    align: 'center',
    customStyles: ({ key }) => checkIsTotalAdjustmentsField(key) && totalAdjustmentsStyles,
  },
});

export const getTotalAmountField = adjustments => {
  const isAdjustmentsChanged = adjustments.some(({ pendingChangesActionType }) =>
    modifiedPendingChanges.includes(pendingChangesActionType),
  );

  return {
    name: TotalAdjustmentsField.NAME,
    key: TotalAdjustmentsField.KEY,
    id: TotalAdjustmentsField.KEY,
    amount: getTotalAmount(adjustments),
    pendingChangesActionType: isAdjustmentsChanged
      ? PendingChangesActionType.MODIFIED
      : PendingChangesActionType.NO_CHANGES,
    modifiedFields: getConditionalArray(isAdjustmentsChanged, ['Amount']),
  };
};

export const customAdjustmentsConfigFields = {
  adjustmentReason: {
    fieldId: 'adjustmentReason',
    type: FieldType.SELECT,
    nameInModel: 'adjustmentReason',
    inputProps: {
      options: [],
      placeholder: 'Select one',
      label: 'Adjustment Reason',
      disablePortal: false,
      required: true,
    },
  },
  adjustmentAmount: {
    fieldId: 'adjustmentAmount',
    type: FieldType.INPUT,
    nameInModel: 'adjustmentAmount',
    inputProps: {
      type: FieldInputType.FLOAT,
      placeholder: 'Please enter',
      label: 'Adjustment Amount',
      required: true,
      decimalScale: 3,
      minValue: 0,
      thousandSeparator: true,
      customSchema: getFloatField,
    },
  },
  adjustmentComment: {
    fieldId: 'adjustmentComment',
    type: FieldType.INPUT,
    nameInModel: 'adjustmentComment',
    inputProps: {
      placeholder: 'Please enter comment',
      label: 'Adjustment Comment',
      multiline: true,
      rows: 4,
      maxLength: 500,
    },
  },
};

export const getCustomAdjustmentsConfig = compose(
  fillConfigFieldsWithKeys,
  ({ customAdjustmentsOptions, shouldShowComment = false, isCommentRequired = false }) => {
    return {
      groups: [
        {
          type: FieldType.GROUP,
          fields: [
            {
              ...customAdjustmentsConfigFields.adjustmentReason,
              inputProps: {
                ...customAdjustmentsConfigFields.adjustmentReason.inputProps,
                options: customAdjustmentsOptions,
              },
            },
            customAdjustmentsConfigFields.adjustmentAmount,
            ...(shouldShowComment
              ? [
                  {
                    ...customAdjustmentsConfigFields.adjustmentComment,
                    inputProps: {
                      ...customAdjustmentsConfigFields.adjustmentComment.inputProps,
                      required: isCommentRequired,
                    },
                  },
                ]
              : []),
          ],
          key: `custom-adjustment`,
        },
      ],
    };
  },
);

export const lockExtensionFields = {
  term: {
    fieldId: 'term',
    type: FieldType.INPUT,
    nameInModel: 'term',
    inputProps: {
      label: 'Extension Term',
      placeholder: 'Please enter',
      required: true,
      type: FieldInputType.INT,
      withRequiredIcon: false,
    },
  },
  amount: {
    type: FieldType.INPUT,
    fieldId: 'amount',
    nameInModel: 'amount',
    inputProps: {
      allowNegative: true,
      fixedDecimalScale: true,
      type: FieldInputType.FLOAT,
      label: 'Extension Amount',
      placeholder: 'Please enter',
      maxValue: 0,
      required: true,
      minValue: MIN_NUMBER_VALUE,
      decimalScale: 3,
      withRequiredIcon: false,
    },
  },
  llpaType: {
    fieldId: 'llpaType',
    type: FieldType.SELECT,
    nameInModel: 'llpaType',
    inputProps: {
      options: [],
      placeholder: 'Choose LLPA Type',
      label: 'Choose LLPA Type',
      required: true,
      withRequiredIcon: false,
    },
  },
};

export const getExtensionConfig = compose(
  fillConfigFieldsWithKeys,
  ({ llpaTypeOptions, isOBFlow }) => {
    const fields = [lockExtensionFields.term];

    if (!isOBFlow) {
      fields.push(lockExtensionFields.amount, {
        ...lockExtensionFields.llpaType,
        inputProps: {
          ...lockExtensionFields.llpaType.inputProps,
          options: llpaTypeOptions,
        },
      });
    }

    return {
      groups: [
        {
          type: FieldType.GROUP,
          fields,
          key: `extension`,
        },
      ],
    };
  },
);

export const markupsTableConfig = {
  name: {
    label: 'Markup Level Name',
    width: '50%',
  },
  finalValue: {
    label: 'Final Markup Value',
    width: '50%',
  },
};
